import React from "react";
import Tracking from "../Tracking/Tracking";
import {HSC_TRACKING} from "../../consts/tables";
import PrintTracking from "../PrintTracking/PrintTracking";
import Drawer from "../Drawer";
import { can } from "../../access";

export const tasks = [{
    id: 'BORROWER_NAME',
    name: 'Borrower Name',
    type: 'static',
    isSticky: true,
}, {
    id: 'LENDER_CONTACT',
    name: 'Lender Contact',
    type: 'vendorSelect',
    vendorType: 'Lender',
    contactNoun: 'contact',
    companyKey: 'LENDER_NAME',
    leadIdKey: 'LENDER_LEAD_ID',
    isSticky: true,
}, {
    id: 'LENDER_NAME',
    name: 'Lender',
    type: 'readOnlyText',
    isSticky: true,
}, {
    id: 'ESCROW_OFFICER',
    name: 'Escrow Officer',
    type: 'vendorSelect',
    // Summit-sourced properties in Loan In Process mirror the Summit escrow
    // board's pick instead of a separate one here.
    lockedByKey: 'ESCROW_INFO_FROM_SUMMIT',
    // step: 12,
}, {
    id: 'TITLE_COMPANY',
    name: 'Title Company',
    type: 'titleCompanySelect',
    // step: 11,
}, {
    id: 'LOAN_TYPE',
    name: 'Loan Type',
    type: 'static',
}, {
    id: 'LOAN_AMOUNT',
    name: 'Loan Amount',
    type: 'static',
}, {
    id: 'QUOTE_SENT_TO_BORROWER',
    name: 'Quote Sent to Borrower',
    type: 'date',
    step: 1,
}, {
    id: 'HSC_AGREEMENT',
    name: 'Agreement',
    type: 'empty',
    step: 2,
}, {
    id: 'HSC_AGREEMENT_SENT',
    name: 'Sent',
    type: 'date',
    isSubTask: true,
}, {
    id: 'HSC_AGREEMENT_SIGNED',
    name: 'Signed',
    type: 'date',
    isSubTask: true,
}, {
    id: 'COLLECT_BORROWER_DOCUMENTS',
    name: 'Collect Borrower Documents',
    type: 'empty',
    step: 3,
}, {
    id: 'COLLECT_BORROWER_DOCUMENTS_REQUESTED',
    name: 'Requested',
    type: 'date',
    isSubTask: true,
}, {
    id: 'COLLECT_BORROWER_DOCUMENTS_RECEIVED',
    name: 'Received',
    type: 'date',
    isSubTask: true,
}, {
    id: 'COLLECT_BORROWER_DOCUMENTS_OUTSTANDING',
    name: 'Outstanding',
    type: 'text',
    isSubTask: true,
}, {
    id: 'TERM_SHEET',
    name: 'Term Sheet',
    type: 'empty',
    step: 4,
}, {
    id: 'TERM_SHEET_SENT',
    name: 'Sent',
    type: 'date',
    isSubTask: true,
}, {
    id: 'TERM_SHEET_SIGNED',
    name: 'Signed',
    type: 'date',
    isSubTask: true,
}, {
    id: 'DEPOSIT_RECEIVED',
    name: 'Deposit Received',
    type: 'date',
}, {
    id: 'ORDER_3RD_PARTIES',
    name: 'Order 3rd Parties',
    type: 'date',
    step: 5,
}, {
    id: 'ORDER_3RD_PARTIES_PHASE_I',
    name: 'Phase I',
    type: 'date',
    isSubTask: true,
}, {
    id: 'ORDER_3RD_PARTIES_PHASE_I_DUE_DATE',
    name: 'Due Date',
    type: 'date',
    isSubTask: true,
}, {
    id: 'ORDER_3RD_PARTIES_APPRAISAL',
    name: 'Appraisal',
    type: 'date',
    isSubTask: true,
}, {
    id: 'ORDER_3RD_PARTIES_APPRAISAL_DUE_DATE',
    name: 'Due Date',
    type: 'date',
    isSubTask: true,
}, {
    id: 'LOAN_COMMITTEE',
    name: 'Loan Committee',
    type: 'date',
    step: 6,
}, {
    id: 'LOAN_COMMITTEE_APPROVED',
    name: 'Approved',
    type: 'dropdown',
    isSubTask: true,
}, {
    id: 'ESTOPPEL_SNDA',
    name: 'Estoppel/SNDA',
    type: 'empty',
    step: 7,
}, {
    id: 'ESTOPPEL_SNDA_SENT',
    name: 'Sent',
    type: 'date',
    isSubTask: true,
}, {
    id: 'ESTOPPEL_SNDA_RECEIVED',
    name: 'Received',
    type: 'date',
    isSubTask: true,
}, {
    id: 'LOAN_DOCS',
    name: 'Loan Docs',
    type: 'date',
    step: 8,
}, {
    id: 'SENT_INVOICE_WIRE_INSTRUCTIONS',
    name: 'Sent Invoice/Wire Instructions',
    type: 'date',
    step: 9,
}, {
    id: 'CLOSE_OF_ESCROW',
    name: 'Close of Escrow',
    type: 'date',
    step: 10,
}];

const dateProps = tasks
    .filter(task => task.type === 'date')
    .map(task => task.id);

const checkboxProps = tasks
    .filter(task => task.type === 'date')
    .map(task => task.id + '_CHECKBOX');

const priceProps = ['LOAN_AMOUNT'];

const excludedProps = ['BORROWER_NAME', 'LOAN_TYPE', 'LOAN_AMOUNT'];

export default class HscTracking extends Tracking {
    drawerComponentRef;

    constructor(props) {
        super(props);

        this.drawerComponentRef = React.createRef();
    }

    /**
     * Peak Tracking: the server's HSC_TRACKING write rule. Edit edits it; none
     * leaves it to the Peak sections; view is read-only.
     */
    editsEveryRow() {
        return can('peak.tracking', 'edit') || (!can('peak.tracking', 'view') && can('property.peak_sections', 'edit'));
    }

    /** The server has no own-deals rule for Peak tracking: view means every row is read-only. */
    editsOwnRows() {
        return false;
    }

    componentDidMount() {
        const propertyId = this.props.location.state || this.props.location.pathname.split('/')[2];
        this.fetch({propertyId, dateProps, checkboxProps, priceProps, title: 'Tracking', isHsc: true});
        this.updateWindowDimensions();
        window.addEventListener('resize', this.updateWindowDimensions);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateWindowDimensions);
    }

    render() {
        const {data, checkboxes} = this.state;
        const checkboxKeys = Object.keys(checkboxes);

        if (data === null) return null;

        const d = new Date();
        const date = d.getMonth() + 1 + '/' + d.getDate() + '/' + d.getFullYear();
        const isOneProperty = !!(this.props.location.state || this.props.location.pathname.split('/')[2]);

        return (
            <div className='Tracking'>
                <Drawer
                    isOpen={this.state.showDrawer}
                    reff={this.drawerComponentRef}
                    closeDrawer={this.closeDrawer}
                >
                    {this.getDrawerComponent()}
                </Drawer>
                <PrintTracking data={data} tasks={tasks} splitTaskId='LOAN_COMMITTEE' title='Tracking'/>
                <div id="Tracking">
                    {this.renderHeader({date, dateProps, checkboxProps, priceProps, excludedProps, tableCode: HSC_TRACKING})}
                    {this.renderTable({data, tasks, checkboxes, checkboxKeys, isOneProperty})}
                </div>
            </div>
        );
    }
}